import React, { useState, useEffect, useCallback } from 'react';
import { colaboradoresApi } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { Star, Trash2, Plus, Loader2 } from 'lucide-react';

const hojeSP = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });

export const ColaboradorOcorrenciasDialog = ({ open, onOpenChange, colaborador, onChanged }) => {
  const [tipos, setTipos] = useState([]);
  const [ocorrencias, setOcorrencias] = useState([]);
  const [tipoId, setTipoId] = useState('');
  const [data, setData] = useState(hojeSP());
  const [obs, setObs] = useState('');
  const [saving, setSaving] = useState(false);
  const [showNovoTipo, setShowNovoTipo] = useState(false);
  const [novoTipoNome, setNovoTipoNome] = useState('');
  const [novoTipoImpacto, setNovoTipoImpacto] = useState('-0.3');
  const [notaManual, setNotaManual] = useState('');

  const load = useCallback(async () => {
    if (!colaborador) return;
    try {
      const [tiposRes, ocRes] = await Promise.all([
        colaboradoresApi.ocorrenciaTipos(),
        colaboradoresApi.ocorrencias(colaborador.id)
      ]);
      setTipos(tiposRes.data);
      setOcorrencias(ocRes.data);
    } catch (err) {
      toast.error('Erro ao carregar ocorrências');
    }
  }, [colaborador]);

  useEffect(() => {
    if (open) {
      load();
      setTipoId('');
      setData(hojeSP());
      setObs('');
      setShowNovoTipo(false);
      setNotaManual(colaborador?.nota_manual != null ? String(colaborador.nota_manual) : '');
    }
  }, [open, load, colaborador]);

  const contagem = ocorrencias.reduce((acc, o) => {
    acc[o.tipo_nome] = (acc[o.tipo_nome] || 0) + 1;
    return acc;
  }, {});

  const handleRegistrar = async () => {
    if (!tipoId) { toast.error('Selecione o tipo de ocorrência'); return; }
    setSaving(true);
    try {
      await colaboradoresApi.addOcorrencia(colaborador.id, { tipo_id: tipoId, data, observacao: obs });
      toast.success('Ocorrência registrada');
      setObs('');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error('Erro ao registrar ocorrência');
    } finally {
      setSaving(false);
    }
  };

  const handleExcluir = async (oid) => {
    try {
      await colaboradoresApi.deleteOcorrencia(colaborador.id, oid);
      toast.success('Ocorrência excluída');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error('Erro ao excluir');
    }
  };

  const handleNovoTipo = async () => {
    if (!novoTipoNome.trim()) { toast.error('Informe o nome do tipo'); return; }
    try {
      const res = await colaboradoresApi.createOcorrenciaTipo({ nome: novoTipoNome.trim(), impacto: parseFloat(novoTipoImpacto) || 0 });
      toast.success('Tipo criado');
      setNovoTipoNome('');
      setShowNovoTipo(false);
      setTipos((p) => [...p, res.data]);
      setTipoId(res.data.id);
    } catch (err) {
      toast.error('Erro ao criar tipo');
    }
  };

  const handleSalvarNota = async (valor) => {
    try {
      await colaboradoresApi.update(colaborador.id, { nota_manual: valor });
      toast.success(valor === null ? 'Voltou para nota automática' : 'Nota manual salva');
      onChanged?.();
    } catch (err) {
      toast.error('Erro ao salvar nota');
    }
  };

  if (!colaborador) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ocorrencias-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star size={18} className="text-amber-500" />
            Avaliação — {colaborador.nome}
          </DialogTitle>
          <DialogDescription>
            Nota automática: <strong>{colaborador.nota_auto?.toFixed(1)}</strong>
            {(colaborador.os_concluidas || 0) > 0 && <> · {colaborador.os_concluidas} OS concluída{colaborador.os_concluidas > 1 ? 's' : ''} (+0,1 cada)</>}
            {colaborador.nota_manual != null && <> · Nota manual ativa: <strong>{Number(colaborador.nota_manual).toFixed(1)}</strong></>}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-end gap-2 rounded-lg border bg-amber-50/50 border-amber-100 p-3">
          <div className="space-y-1 flex-1">
            <Label className="text-xs">Nota manual (0 a 5) — sobrepõe a automática</Label>
            <Input type="number" min="0" max="5" step="0.1" value={notaManual} onChange={(e) => setNotaManual(e.target.value)} placeholder="ex.: 4.5" data-testid="nota-manual-input" />
          </div>
          <Button size="sm" onClick={() => handleSalvarNota(Math.max(0, Math.min(5, parseFloat(notaManual) || 0)))} disabled={notaManual === ''} data-testid="salvar-nota-btn">Salvar</Button>
          {colaborador.nota_manual != null && (
            <Button size="sm" variant="outline" onClick={() => { setNotaManual(''); handleSalvarNota(null); }} data-testid="limpar-nota-btn">Usar automática</Button>
          )}
        </div>

        {Object.keys(contagem).length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="ocorrencias-contadores">
            {Object.entries(contagem).map(([nome, qtd]) => (
              <Badge key={nome} variant="outline" className={/elogio/i.test(nome) ? 'border-emerald-300 text-emerald-700' : 'border-red-200 text-red-600'}>
                {nome}: {qtd}x
              </Badge>
            ))}
          </div>
        )}

        <div className="space-y-2 rounded-lg border p-3 bg-gray-50">
          <div className="flex items-center justify-between">
            <Label className="font-semibold">Registrar ocorrência</Label>
            <button type="button" onClick={() => setShowNovoTipo((v) => !v)} className="text-xs text-emerald-700 hover:underline flex items-center gap-1" data-testid="toggle-novo-tipo">
              <Plus size={12} /> Novo tipo
            </button>
          </div>
          {showNovoTipo && (
            <div className="flex items-end gap-2 rounded-md border bg-white p-2">
              <div className="flex-1 space-y-1">
                <Label className="text-xs">Nome do tipo</Label>
                <Input value={novoTipoNome} onChange={(e) => setNovoTipoNome(e.target.value)} placeholder="ex.: Não usou uniforme" data-testid="novo-tipo-nome" />
              </div>
              <div className="w-28 space-y-1">
                <Label className="text-xs">Impacto na nota</Label>
                <Input type="number" step="0.1" min="-1" max="1" value={novoTipoImpacto} onChange={(e) => setNovoTipoImpacto(e.target.value)} data-testid="novo-tipo-impacto" />
              </div>
              <Button size="sm" onClick={handleNovoTipo} data-testid="criar-tipo-btn">Criar</Button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Tipo *</Label>
              <Select value={tipoId || undefined} onValueChange={setTipoId}>
                <SelectTrigger data-testid="ocorrencia-tipo-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {tipos.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.nome} ({t.impacto > 0 ? '+' : ''}{t.impacto})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Data</Label>
              <Input type="date" value={data} onChange={(e) => setData(e.target.value)} data-testid="ocorrencia-data-input" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Observação (opcional)</Label>
            <Textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={2} data-testid="ocorrencia-obs-input" />
          </div>
          <Button onClick={handleRegistrar} disabled={saving} className="w-full text-white" style={{ backgroundColor: '#2B7A3D' }} data-testid="registrar-ocorrencia-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Registrar ocorrência'}
          </Button>
        </div>

        <div className="space-y-1">
          <Label className="font-semibold text-sm">Histórico ({ocorrencias.length})</Label>
          {ocorrencias.length === 0 && <p className="text-sm text-gray-400">Nenhuma ocorrência registrada.</p>}
          <div className="space-y-1.5 max-h-60 overflow-y-auto">
            {ocorrencias.map((o) => (
              <div key={o.id} className="flex items-start justify-between gap-2 rounded-md border p-2 text-sm" data-testid={`ocorrencia-item-${o.id}`}>
                <div>
                  <span className={`font-medium ${o.impacto >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{o.tipo_nome}</span>
                  <span className="text-gray-400 ml-2 text-xs">{formatDate(o.data)}</span>
                  {o.observacao && <p className="text-gray-500 text-xs mt-0.5">{o.observacao}</p>}
                </div>
                <button onClick={() => handleExcluir(o.id)} title="Excluir" data-testid={`excluir-ocorrencia-${o.id}`}>
                  <Trash2 size={14} className="text-red-400 hover:text-red-600" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
